import { useState, MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  makeStyles,
} from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import {
  errorApiRef,
  identityApiRef,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';
import {
  sidebarConfig,
  useSidebarOpenState,
} from '@backstage/core-components';
import { useUserProfile } from '@backstage/plugin-user-settings';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 48,
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(0.5),
    boxSizing: 'border-box',
  },
  rootClosed: {
    justifyContent: 'center',
    padding: theme.spacing(0.5, 0),
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    width: '100%',
    maxWidth: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.default,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    padding: theme.spacing(0.75, 1),
    textAlign: 'left',
    transition: theme.transitions.create(['background-color', 'border-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      background: theme.palette.action.hover,
      borderColor: theme.palette.text.hint || theme.palette.divider,
    },
  },
  buttonClosed: {
    width: sidebarConfig.iconSize + 12,
    height: sidebarConfig.iconSize + 12,
    justifyContent: 'center',
    padding: 0,
    borderRadius: '50%',
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: '0.85rem',
    fontWeight: 600,
    flexShrink: 0,
  },
  avatarClosed: {
    width: sidebarConfig.iconSize,
    height: sidebarConfig.iconSize,
    fontSize: '0.8rem',
  },
  textBlock: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
  },
  name: {
    fontWeight: 600,
    fontSize: '0.85rem',
    lineHeight: 1.2,
  },
  email: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    lineHeight: 1.2,
  },
  menuPaper: {
    minWidth: 280,
    marginTop: theme.spacing(0.5),
  },
  profileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    maxWidth: 320,
  },
  profileAvatar: {
    width: 48,
    height: 48,
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  profileText: {
    minWidth: 0,
    flex: 1,
  },
  menuName: {
    fontWeight: 700,
    lineHeight: 1.25,
  },
  menuEmail: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    wordBreak: 'break-all',
  },
  identity: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    marginTop: 2,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
}));

/** Profile control in the sidebar header — opens details / settings / sign out. */
export const UserProfileMenu = () => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const identityApi = useApi(identityApiRef);
  const errorApi = useApi(errorApiRef);
  const analytics = useAnalytics();
  const { profile, displayName, loading, backstageIdentity } = useUserProfile();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();

  const menuOpen = Boolean(anchorEl);
  const initial = (displayName || profile.email || '?').charAt(0).toUpperCase();
  const entityRef = backstageIdentity?.userEntityRef;

  if (loading) {
    return null;
  }
  if (!displayName && !profile.email && !profile.picture && !entityRef) {
    return null;
  }

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  const handleSignOut = () => {
    handleClose();
    identityApi.signOut().catch(error => errorApi.post(error));
    analytics.captureEvent('signOut', 'success');
  };

  return (
    <div className={`${classes.root} ${isOpen ? '' : classes.rootClosed}`}>
      <button
        type="button"
        className={`${classes.button} ${isOpen ? '' : classes.buttonClosed}`}
        onClick={handleOpen}
        aria-label="Open user profile menu"
        aria-controls={menuOpen ? 'user-profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
        title={displayName || 'User profile'}
      >
        <Avatar
          className={`${classes.avatar} ${isOpen ? '' : classes.avatarClosed}`}
          src={profile.picture}
          alt={displayName || 'User'}
        >
          {initial}
        </Avatar>
        {isOpen && (
          <div className={classes.textBlock}>
            <Typography className={classes.name} noWrap>
              {displayName || 'Signed in'}
            </Typography>
            {profile.email && (
              <Typography className={classes.email} noWrap>
                {profile.email}
              </Typography>
            )}
          </div>
        )}
      </button>

      <Menu
        id="user-profile-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        keepMounted
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isOpen ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isOpen ? 'left' : 'left',
        }}
        PaperProps={{ className: classes.menuPaper }}
      >
        <Box className={classes.profileHeader}>
          <Avatar
            className={classes.profileAvatar}
            src={profile.picture}
            alt={displayName || 'User'}
          >
            {initial}
          </Avatar>
          <div className={classes.profileText}>
            <Typography className={classes.menuName} noWrap>
              {displayName || 'Signed in'}
            </Typography>
            {profile.email && (
              <Typography className={classes.menuEmail}>
                {profile.email}
              </Typography>
            )}
            {entityRef && (
              <Typography className={classes.identity} noWrap title={entityRef}>
                {entityRef}
              </Typography>
            )}
          </div>
        </Box>

        <Divider />

        <MenuItem component={RouterLink} to="/settings" onClick={handleClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile & settings" />
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>
    </div>
  );
};
